import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'
import type {CatalogItem} from '@/hooks/sgsst/incidentes/use-investigacion'
import {usePagedList} from '@/hooks/common/use-paged-list'

export const INCIDENTES_PAGE_SIZE = 20

export type Incidente = components['schemas']['IncidenteRead']
export type IncidenteListItem = components['schemas']['IncidenteListItem']
export type IncidenteReloj = components['schemas']['IncidenteRelojRead']
export type IncidenteCreate = components['schemas']['IncidenteCreate']
export type IncidenteTipo = components['schemas']['IncidenteTipo']
export type IncidenteGravedad = components['schemas']['IncidenteGravedad']
export type IncidenteOrigen = components['schemas']['IncidenteOrigen']
export type IncidentePatch = components['schemas']['IncidentePatch']
export type IncidenteTransition = components['schemas']['IncidenteTransition']
export type Afectado = components['schemas']['AfectadoRead']
export type AfectadoPayload = components['schemas']['AfectadoPayload']
export type AfectadoPatch = components['schemas']['AfectadoPatch']
export type AfectadoGravedad = components['schemas']['AfectadoGravedad']
export type EmpleadoEvento = components['schemas']['EmpleadoEventoRead']
export type AfectadoPersona = components['schemas']['AfectadoPersona']

export type IncidenteListPage = components['schemas']['IncidenteList']

// El orden peor-primero es una regla del dominio, no un sort elegible: solo hay página. Sin
// `year`, la lista actual.
export function useIncidentes(businessId: string | undefined, year?: number, page = 1) {
  return useQuery({
    queryKey: ['incidentes', businessId, year ?? 'all', page],
    queryFn: async () =>
      (
        await api.get<IncidenteListPage>(`/businesses/${businessId}/incidentes`, {
          params: {year, page, page_size: INCIDENTES_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId,
    placeholderData: (previous) => previous,
  })
}

// `page_size: 1` porque solo se usa `total`.
export function useIncidentesAbiertos(businessId: string | undefined) {
  return useQuery({
    queryKey: ['incidentes-abiertos', businessId],
    queryFn: async () =>
      (
        await api.get<IncidenteListPage>(`/businesses/${businessId}/incidentes`, {
          params: {estado_ne: 'cerrado', page_size: 1},
        })
      ).data.total,
    enabled: !!businessId,
  })
}

export function useDeleteIncidente(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({incidenteId, motivo}: {incidenteId: string; motivo: string}) => {
      await api.post(`/businesses/${businessId}/incidentes/${incidenteId}/eliminar`, {motivo})
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['incidentes', businessId]})
      queryClient.invalidateQueries({queryKey: ['incidentes-abiertos', businessId]})
      queryClient.invalidateQueries({queryKey: ['indicadores', businessId]})
      queryClient.invalidateQueries({queryKey: ['indicadores-serie', businessId]})
    },
  })
}

export function useIncidente(businessId: string | undefined, incidenteId: string | undefined) {
  return useQuery({
    queryKey: ['incidente', businessId, incidenteId],
    queryFn: async () => (await api.get<Incidente>(`/businesses/${businessId}/incidentes/${incidenteId}`)).data,
    enabled: !!businessId && !!incidenteId,
  })
}

// Solo nombre y área: `/employees/all` es solo de operador, y sin esto un rol `user` vería el
// selector vacío.
export function useAfectadoPersonas(businessId: string | undefined) {
  return useQuery({
    queryKey: ['incidente-personas', businessId],
    queryFn: async () => (await api.get<AfectadoPersona[]>(`/businesses/${businessId}/incidentes/personas`)).data,
    enabled: !!businessId,
  })
}

// Cualquier miembro puede reportar (D-2).
export function useReportarIncidente(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: IncidenteCreate) =>
      (await api.post<Incidente>(`/businesses/${businessId}/incidentes`, payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['incidentes', businessId]})
      queryClient.invalidateQueries({queryKey: ['notifications']})
    },
  })
}

function useIncidenteAction<TBody>(businessId: string, incidenteId: string, path: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body?: TBody) =>
      (await api.post<Incidente>(`/businesses/${businessId}/incidentes/${incidenteId}/${path}`, body ?? {})).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['incidente', businessId, incidenteId]})
      queryClient.invalidateQueries({queryKey: ['incidentes', businessId]})
      queryClient.invalidateQueries({queryKey: ['notifications']})
    },
  })
}

export function useGenerarFurat(businessId: string, incidenteId: string) {
  return useIncidenteAction<void>(businessId, incidenteId, 'furat/generar')
}

export function useMarcarFuratManual(businessId: string, incidenteId: string) {
  return useIncidenteAction<{radicado: string; radicado_fecha: string}>(businessId, incidenteId, 'furat/manual')
}

export function useMarcarTerritorial(businessId: string, incidenteId: string) {
  return useIncidenteAction<void>(businessId, incidenteId, 'territorial')
}

export function useTransicionarIncidente(businessId: string, incidenteId: string) {
  return useIncidenteAction<IncidenteTransition>(businessId, incidenteId, 'transicion')
}

// Detiene su reloj y arranca los del concepto de ARL y Ministerio (grave o mortal).
export function useMarcarInformeArl(businessId: string, incidenteId: string) {
  return useIncidenteAction<void>(businessId, incidenteId, 'informe-arl')
}

// Solo aplica a tipo=accidente: para incidente es 404.
export function furatDocumentoUrl(businessId: string, incidenteId: string): string {
  return `/businesses/${businessId}/incidentes/${incidenteId}/furat/documento`
}

export function usePatchIncidente(businessId: string, incidenteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (patch: IncidentePatch) =>
      (await api.patch<Incidente>(`/businesses/${businessId}/incidentes/${incidenteId}`, patch)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['incidente', businessId, incidenteId]})
      queryClient.invalidateQueries({queryKey: ['incidentes', businessId]})
    },
  })
}

// Catálogo de tipos de lesión (NTC 3701 Anexo A): deriva la gravedad legal.
export function useLesionesCatalog(businessId: string | undefined) {
  return useQuery({
    queryKey: ['catalogo-lesiones', businessId],
    queryFn: async () => (await api.get<CatalogItem[]>(`/businesses/${businessId}/catalogos/lesiones`)).data,
    enabled: !!businessId,
  })
}

function afectadosBase(businessId: string, incidenteId: string): string {
  return `/businesses/${businessId}/incidentes/${incidenteId}/investigacion/afectados`
}

function useAfectadosInvalidation(businessId: string, incidenteId: string) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({queryKey: ['investigacion', businessId, incidenteId]})
    queryClient.invalidateQueries({queryKey: ['incidente', businessId, incidenteId]})
    queryClient.invalidateQueries({queryKey: ['indicadores', businessId]})
  }
}

export function useAddAfectado(businessId: string, incidenteId: string) {
  const invalidate = useAfectadosInvalidation(businessId, incidenteId)
  return useMutation({
    mutationFn: async (payload: AfectadoPayload) =>
      (await api.post<Afectado>(afectadosBase(businessId, incidenteId), payload)).data,
    onSuccess: invalidate,
  })
}

export function usePatchAfectado(businessId: string, incidenteId: string) {
  const invalidate = useAfectadosInvalidation(businessId, incidenteId)
  return useMutation({
    mutationFn: async (input: {afectadoId: string; patch: AfectadoPatch}) =>
      (await api.patch<Afectado>(`${afectadosBase(businessId, incidenteId)}/${input.afectadoId}`, input.patch)).data,
    onSuccess: invalidate,
  })
}

// Permite quitar incluso al último afectado.
export function useRemoveAfectado(businessId: string, incidenteId: string) {
  const invalidate = useAfectadosInvalidation(businessId, incidenteId)
  return useMutation({
    mutationFn: async (afectadoId: string) => api.delete(`${afectadosBase(businessId, incidenteId)}/${afectadoId}`),
    onSuccess: invalidate,
  })
}

const EVENTOS_PAGE_SIZE = 10

export function useEmpleadoEventos(
  businessId: string | undefined,
  employeeId: string | undefined,
  page = 1,
  // El selector de accidente filtra en el navegador (R3): con el corte de diez no vería los
  // antiguos, así que pide el tope de `PaginaSola`.
  pageSize = EVENTOS_PAGE_SIZE
) {
  return usePagedList<EmpleadoEvento>(
    ['empleado-eventos', businessId, employeeId],
    businessId && employeeId ? `/businesses/${businessId}/employees/${employeeId}/eventos` : undefined,
    {page, search: ''},
    pageSize
  )
}

/** Tope de `PaginaSola` (R6: nunca > 50), para el selector que no pagina. */
export const EVENTOS_SELECTOR_PAGE_SIZE = 50
